import { useEffect, useImperativeHandle, useRef, useState, forwardRef, type KeyboardEvent } from 'react';
import { Send, Square, Mic, MicOff, Paperclip, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { WorkbookContext } from '@/types/chat';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import type { CapturedImage } from '@/services/office';
import { uploadAttachment, type Attachment, MAX_ATTACHMENT_BYTES } from '@/services/attachments';
import { extractErrorMessage } from '@/services/api';

interface Props {
  onSend: (msg: string, images?: CapturedImage[], attachmentIDs?: string[]) => void;
  onCancel: () => void;
  streaming: boolean;
  workbookContext: WorkbookContext;
  // Kept on the props shape for callers that still pass it (camera
  // button removed; flag no longer affects rendering).
  visionSupported?: boolean;
}

// Match backend allowlist. Extension-based fallback handles browsers
// that mislabel CSVs as application/octet-stream.
const ACCEPTED_FILE_TYPES =
  '.csv,.xlsx,.pdf,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,text/plain,text/markdown,image/*';
const MAX_ATTACHMENTS_PER_TURN = 4;

// InputBoxHandle exposes a focused .setText() so parent components
// (templates, voice, clarifications) can drop a prompt into the box
// without us plumbing controlled-value state through every caller.
export interface InputBoxHandle {
  setText: (text: string) => void;
  focus: () => void;
}

export const InputBox = forwardRef<InputBoxHandle, Props>(function InputBox(
  { onSend, onCancel, streaming, workbookContext },
  ref,
) {
  const [value, setValue] = useState('');
  const [pendingImages, setPendingImages] = useState<CapturedImage[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    if (streaming || uploading) return;
    if (pendingAttachments.length >= MAX_ATTACHMENTS_PER_TURN) {
      toast.error(`Max ${MAX_ATTACHMENTS_PER_TURN} attachments per message`);
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Clear the native input so re-selecting the same file still fires a change event.
    e.target.value = '';
    if (files.length === 0) return;

    const slots = MAX_ATTACHMENTS_PER_TURN - pendingAttachments.length;
    if (slots <= 0) {
      toast.error(`Max ${MAX_ATTACHMENTS_PER_TURN} attachments per message`);
      return;
    }
    const queue = files.slice(0, slots);
    if (files.length > slots) {
      toast.warning(`Only first ${slots} of ${files.length} files uploaded`);
    }

    setUploading(true);
    for (const file of queue) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${file.name}: exceeds 10 MB cap`);
        continue;
      }
      try {
        const att = await uploadAttachment(file);
        setPendingAttachments((prev) => [...prev, att]);
      } catch (err) {
        toast.error(extractErrorMessage(err, `Upload failed: ${file.name}`));
      }
    }
    setUploading(false);
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const removeImage = (idx: number) => {
    setPendingImages((prev) => prev.filter((_, i) => i !== idx));
  };

  useImperativeHandle(ref, () => ({
    setText: (text) => {
      setValue(text);
      // Defer focus until the textarea has re-rendered with the new value
      // so the caret lands at the end of the inserted prompt.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(text.length, text.length);
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    focus: () => inputRef.current?.focus(),
  }));

  // Auto-grow the textarea as the user types / a template fills it.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, [value]);

  // Voice input — captures a snapshot of the pre-dictation text so
  // interim transcripts don't destroy what the user already typed.
  const baseTextRef = useRef('');
  const speech = useSpeechRecognition({
    onInterim: (text) => {
      const sep = baseTextRef.current && !baseTextRef.current.endsWith(' ') ? ' ' : '';
      setValue(baseTextRef.current + sep + text);
    },
    onFinal: (text) => {
      const sep = baseTextRef.current && !baseTextRef.current.endsWith(' ') ? ' ' : '';
      const next = baseTextRef.current + sep + text;
      setValue(next);
      baseTextRef.current = next;
      inputRef.current?.focus();
    },
  });

  useEffect(() => {
    if (speech.error) toast.error(speech.error);
  }, [speech.error]);

  const toggleMic = () => {
    if (!speech.supported) {
      toast.error('Voice input is not supported in this browser');
      return;
    }
    if (speech.listening) {
      speech.stop();
      return;
    }
    baseTextRef.current = value;
    speech.start();
  };

  const submit = () => {
    const msg = value.trim();
    const hasContent = msg || pendingImages.length > 0 || pendingAttachments.length > 0;
    if (!hasContent || streaming || uploading) return;
    const imgs = pendingImages.length > 0 ? pendingImages : undefined;
    const attIDs = pendingAttachments.length > 0 ? pendingAttachments.map((a) => a.id) : undefined;
    const defaultPrompt = pendingImages.length > 0
      ? 'What do you see in this screenshot?'
      : 'What is in these file(s)?';
    onSend(msg || defaultPrompt, imgs, attIDs);
    setValue('');
    setPendingImages([]);
    setPendingAttachments([]);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const ctxLabel = workbookContext.active_cell
    ? `${workbookContext.active_sheet ?? 'Sheet'}!${workbookContext.active_cell}`
    : null;

  const fmtBytes = (n: number): string => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="border-t border-border bg-white p-2 dark:bg-[#1A1A1A] dark:border-border-dark">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        onChange={onFileSelected}
      />
      {pendingAttachments.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {pendingAttachments.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-1.5 rounded-md border border-border bg-black/5 px-2 py-1 text-[10px] dark:border-border-dark dark:bg-white/5"
              title={`${a.filename} · ${a.mime_type} · ${fmtBytes(a.size_bytes)}`}
            >
              <Paperclip className="h-3 w-3 text-text-secondary" />
              <span className="max-w-[140px] truncate font-medium">{a.filename}</span>
              <span className="text-text-secondary">{fmtBytes(a.size_bytes)}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                aria-label={`Remove ${a.filename}`}
                title="Remove"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {pendingImages.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {pendingImages.map((img, i) => (
            <div
              key={i}
              className="relative h-14 w-20 overflow-hidden rounded-md border border-border bg-black/5 dark:border-border-dark dark:bg-white/5"
            >
              <img
                src={`data:${img.mediaType};base64,${img.data}`}
                alt={img.label}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1 py-0.5 text-[9px] text-white">
                {img.label}
              </div>
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                aria-label="Remove screenshot"
                title="Remove"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="relative">
        <textarea
          ref={inputRef}
          className="input resize-none pr-10"
          rows={2}
          placeholder={ctxLabel ? `Ask about ${ctxLabel}…` : 'Ask anything about your workbook…'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          disabled={streaming}
        />
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          <button
            type="button"
            onClick={openFilePicker}
            disabled={streaming || uploading || pendingAttachments.length >= MAX_ATTACHMENTS_PER_TURN}
            className="flex h-7 w-7 items-center justify-center rounded-md bg-black/5 text-text-secondary transition hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20 disabled:opacity-40"
            aria-label="Attach file"
            title={
              pendingAttachments.length >= MAX_ATTACHMENTS_PER_TURN
                ? `Max ${MAX_ATTACHMENTS_PER_TURN} attachments per message`
                : 'Attach file (Excel, CSV, PDF, text, images · 10 MB max)'
            }
          >
            {uploading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Paperclip className="h-3.5 w-3.5" />}
          </button>
          {speech.supported && (
            <button
              type="button"
              onClick={toggleMic}
              disabled={streaming}
              className={[
                'flex h-7 w-7 items-center justify-center rounded-md transition',
                speech.listening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-black/5 text-text-secondary hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20',
              ].join(' ')}
              aria-label={speech.listening ? 'Stop dictation' : 'Start dictation'}
              title={speech.listening ? 'Stop dictation' : 'Dictate (English/Hindi/Hinglish)'}
            >
              {speech.listening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </button>
          )}
          <button
            onClick={streaming ? onCancel : submit}
            disabled={
              !streaming &&
              !value.trim() &&
              pendingImages.length === 0 &&
              pendingAttachments.length === 0
            }
            className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-white disabled:opacity-40"
            aria-label={streaming ? 'Stop' : 'Send'}
          >
            {streaming ? <Square className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10px] text-text-secondary">
        {ctxLabel && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
            Currently on {ctxLabel}
          </span>
        )}
        {speech.listening && (
          <span className="inline-flex items-center gap-1 text-red-500">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
            Listening…
          </span>
        )}
      </div>
    </div>
  );
});
